// alternative-components/CFOCopilot/LiquidityInfoCard.tsx
import { useEffect, useState } from "react";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardBalanceAccountsApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import InfoCard from "./InfoCard";

interface LiquidityInfoCardProps {
  title?: string;
}

const LiquidityInfoCard = ({ title }: LiquidityInfoCardProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";

  const [headline, setHeadline] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!currencyCode) return;
    let isMounted = true;

    const loadLiquidity = async () => {
      setIsLoading(true);

      try {
        const response = await gxDashboardBalanceAccountsApi({
          currencyCode,
        });
        if (!isMounted) return;

        if (response?.status === 1 && response.data) {
          const {
            netPosition,
            month,
            liquidityPercentage,
            liquidityStatus,
            currency,
          } = response.data;

          const isNegative = liquidityStatus === "NET_NEGATIVE";

          const formattedNetMagnitude = gxCurrencyFormat(
            Math.abs(netPosition),
            currency,
          ).integer;
          const formattedNet = `${isNegative ? "-" : ""}${formattedNetMagnitude}`;

          const signedLiquidityPercentage = liquidityPercentage;

          setHeadline(
            `${formattedNet} net ${isNegative ? "negative" : "positive"} in ${month}`,
          );
          setDescription(
            isNegative
              ? `Outflows outpaced inflows by ${signedLiquidityPercentage}%. Review upcoming payments to protect your buffer.`
              : `Inflows outpaced outflows by ${signedLiquidityPercentage}%. Consider sweeping idle cash to a deposit.`,
          );
        } else {
          setHeadline("");
          setDescription("No liquidity data available");
        }
      } catch (err) {
        if (isMounted) {
          setHeadline("");
          setDescription("Failed to load liquidity insight");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLiquidity();

    return () => {
      isMounted = false;
    };
  }, [currencyCode]);

  return (
    <InfoCard
      title={title || "LIQUIDITY"}
      color="#2B4DED"
      headline={isLoading ? "Loading..." : headline}
      description={isLoading ? "" : description}
    />
  );
};

export default LiquidityInfoCard;
